import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { UpdateCampaignLineItemsFormSchema } from 'c-main/Types';
import { FormWrapper } from 'c-components';
import { NetworkRequestState } from 'c-data-layer';
import type { Campaign } from 'c-sdk';
import apiClient from 'c-data/apiClient';
import to from 'await-to-js';
import { generatePath, useHistory } from 'react-router-dom';
import { buildSyncLineItemsRequest, editCampaignLineItemsDefaults } from 'c-main/Lib';
import { usePrevious } from 'react-hanger';
import { PostAuthRoutes } from 'c-routes';
import { useAPIClientRequest } from 'c-hooks';
import { useEntityData, usePaginatedEntityData } from 'c-data';

import EditCampaignFormStepper from './EditCampaignFormStepper';
import CampaignUpdateState from './CampaignUpdateState';

type Props = {
    campaign: Campaign;
    source: string;
    redirectOnSuccess?: boolean;
    onSuccess?: () => void;
    /** See `FormWrapper`'s `onDirtyChange`; pass a stable callback. */
    onDirtyChange?: (isDirty: boolean) => void;
};

const EditCampaignLineItems: React.FC<Props> = ({
    campaign,
    source,
    redirectOnSuccess = true,
    onSuccess,
    onDirtyChange,
}) => {
    const { upsertEntity } = useEntityData<Campaign>('Campaign');
    const displaysPaginationTag = useMemo(() => `campaign-displays-${campaign.id}`, [campaign.id]);
    const { refresh: refreshCampaignDisplays } = usePaginatedEntityData(
        displaysPaginationTag,
        'Displays_LineItem',
        payload => apiClient.Entities.Campaign.lineItems(campaign.id, payload),
    );
    const defaultFormData = useMemo(
        () => ({ defaultValues: editCampaignLineItemsDefaults(campaign) }),
        [campaign],
    );

    const [loadingState, setLoadingState] = useState<NetworkRequestState>(NetworkRequestState.Idle);
    const { start } = useAPIClientRequest(apiClient.Entities.Campaign.syncLineItems);

    const onSubmit = useCallback(
        async (data: UpdateCampaignLineItemsFormSchema) => {
            const modifiedFormData = buildSyncLineItemsRequest(data);

            setLoadingState(NetworkRequestState.InProgress);

            const [err, success] = await to(start(campaign.id, modifiedFormData, source));

            if (!err && success?.data?.data?.id === campaign.id) {
                upsertEntity(success.data.data as unknown as Campaign);
                refreshCampaignDisplays();
                setLoadingState(NetworkRequestState.Success);
            } else {
                setLoadingState(NetworkRequestState.Error);
            }
        },
        [campaign.id, refreshCampaignDisplays, source, start, upsertEntity],
    );

    const $history = useHistory();
    const prevLoadingState = usePrevious(loadingState);
    useEffect(() => {
        if (
            prevLoadingState != null &&
            prevLoadingState === NetworkRequestState.InProgress &&
            loadingState === NetworkRequestState.Success
        ) {
            onSuccess?.();
            if (redirectOnSuccess) {
                $history.push(
                    generatePath(PostAuthRoutes.Campaigns.CampaignOverview, { id: campaign.id }),
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingState, redirectOnSuccess, onSuccess]);
    return (
        <>
            <FormWrapper
                onSubmit={onSubmit}
                formOptions={defaultFormData}
                onDirtyChange={onDirtyChange}
            >
                <EditCampaignFormStepper campaign={campaign} />
            </FormWrapper>
            <CampaignUpdateState loadingState={loadingState} />
        </>
    );
};

export default EditCampaignLineItems;
